import type { ApiScanNote } from '../ApiSchemas/RecordTypes/ApiScanNoteSchema';
import type { ApiUserOrgRole } from '../ApiSchemas/RecordTypes/ApiUserOrgSchema';

/**
 * The note types a person writes as a remark: the ones a person can edit and delete, and the
 * only ones whose visibility a writer chooses. Status notes (`approval`, `editRequest`, `editResolution`) are decisions in a review
 * workflow and are retracted through that workflow (e.g. `useCreateUnapprovalNote`, which any
 * editor may need to fire when they change an approved task), so they stay outside these rules.
 */
export const isAuthoredRemarkScanNote = (note: Pick<ApiScanNote, 'scanNoteType'>): boolean =>
  note.scanNoteType === 'info' || note.scanNoteType === 'instructions';

export type ScanNotePermissionActor = {
  userId: string | undefined;
  role: ApiUserOrgRole | undefined;
};

/** Managers and admins may delete anyone's notes, but never edit them. */
const canModerateScanNotes = ({ role }: ScanNotePermissionActor): boolean => role === 'admin' || role === 'manager';

/** Only a note's author can edit it. */
export const canUserEditScanNote = (actor: ScanNotePermissionActor, note: Pick<ApiScanNote, 'userId'>): boolean =>
  !!actor.userId && actor.userId === note.userId;

/** A note's author can delete it, and so can a manager or admin. */
export const canUserDeleteScanNote = (actor: ScanNotePermissionActor, note: Pick<ApiScanNote, 'userId'>): boolean =>
  canUserEditScanNote(actor, note) || canModerateScanNotes(actor);
